import React from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { BusyStore } from 'ui/base/button/store';
import { ButtonProps as ReactButtonProps } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import baseStyles from './button.module.css';

type ButtonStyles = {
  button: string;
  active: string;
  link: string;
  disabledButton: string;
  loadingIcon: string;
  reserveLoadingIcon: string;
};

const themedStyles: ThemedStyles<ButtonStyles> = {
  [Theme.DAYLIGHT]: {
    button: baseStyles.daylightButton,
    active: baseStyles.daylightActive,
    link: baseStyles.daylightLink,
    disabledButton: baseStyles.disabledButton,
    loadingIcon: baseStyles.loadingIcon,
    reserveLoadingIcon: baseStyles.reserveLoadingIcon,
  },
  [Theme.MIDNIGHT]: {
    button: baseStyles.midnightButton,
    active: baseStyles.midnightActive,
    link: baseStyles.midnightLink,
    disabledButton: baseStyles.disabledButton,
    loadingIcon: baseStyles.loadingIcon,
    reserveLoadingIcon: baseStyles.reserveLoadingIcon,
  },
};

export enum ButtonLook {
  PLAIN,
  ACTIVE,
  DEFAULT,
  LINK,
}

// properties visible to users of this button
export type ExtendedReactButtonProps = ReactButtonProps & {
  busy?: BusyStore | boolean;
  reserveBusyIconArea?: boolean;
};

// internal properties
export type BaseButtonProps = ExtendedReactButtonProps & {
  look: ButtonLook;
};

export const BaseButton = observer(({ children, className, look, busy, reserveBusyIconArea, ...props }: BaseButtonProps) => {
  const styles = useStyles(themedStyles);
  const busyValue = busy instanceof BusyStore ? busy.value : busy;
  if (busyValue) {
    // don't allow clicking while busy
    props.disabled = true;
  }
  return (
    <button
      className={classNames(className, {
        [styles.button]: look !== ButtonLook.PLAIN,
        [styles.active]: look === ButtonLook.ACTIVE,
        [styles.link]: look === ButtonLook.LINK,
        [styles.disabledButton]: props.disabled,
      })}
      {...props}
    >
      {busyValue ?
        <span className={styles.loadingIcon}/> :
        reserveBusyIconArea ?
          <span className={styles.reserveLoadingIcon}/> :
          null
      } {children}
    </button>
  );
});

export type ButtonProps = ExtendedReactButtonProps;
